import { WebSocketClient } from "./WebSocketClient";
import { MessageType, WebSocketMessage } from "../models/Messages";
import GlobalStore from "./GlobalStore";
import { Ticker } from "pixi.js";

/**
 * WebSocket Simulator for exhibition mode
 * Simulates WebSocket messages without requiring a real WebSocket connection
 */
export class WebSocketSimulator {
  private wsClient: WebSocketClient;
  // private simulationInterval: number | null = null; // No longer needed - using PixiJS ticker
  private simulationData: any = null;
  private isRunning: boolean = false;
  private lastHitTime: number = 0;
  private hitCooldown: number = 90000; // 90 seconds between hits
  private lastWinTime: number = 0; // Track when last win occurred
  private winInterval: number = 120000; // 120 seconds (2 minutes) between wins for demo
  private currentJackpotValues: { code: number; value: number }[] = [];
  private jackpotMinValue: number = 15000000;
  private jackpotMaxValue: number = 18000000;

  private updateElapsed: number = 0;
  private updateInterval: number = 2000; // Default: 2 seconds between updates
  private historySent: boolean = false;
  private historyDelayElapsed: number = 0;
  private historyDelay: number = 2000;

  // Jackpot speed control settings
  private minIncrease: number = 100; // Minimum increase amount (in cents = $10.00)
  private maxIncrease: number = 200; // Maximum increase amount (in cents = $100.00)
  private increaseSpeed: number = 3; // Speed multiplier (1.0 = normal, 2.0 = 2x faster)

  // Performance tracking
  private lastIncreaseAmount: number = 0;
  private increasesPerMinute: number = 0;
  private lastIncreaseTime: number = 0;

  constructor(wsClient: WebSocketClient) {
    this.wsClient = wsClient;
    this.lastWinTime = Date.now(); // Initialize to current time

    Ticker.shared.add(this.onTick);
  }

  public async initialize(): Promise<void> {
    try {
      console.log("🚀 Initializing WebSocket Simulator...");

      // Try to load simulation config
      const response = await fetch("/simulator-config.json");
      if (response.ok) {
        this.simulationData = await response.json();
        console.log("✅ Loaded simulation config:", this.simulationData);
      } else {
        console.warn(
          "⚠️  No simulator-config.json found, using default simulation data",
        );
        this.simulationData = this.getDefaultSimulationData();
      }
    } catch (error) {
      console.warn("❌ Failed to load simulator config:", error);
      this.simulationData = this.getDefaultSimulationData();
    }
  }

  public start(): void {
    if (this.isRunning) return;
    this.isRunning = true;

    // Reset all timing variables
    this.lastHitTime = 0;
    this.updateElapsed = 0;
    this.historySent = false;
    this.historyDelayElapsed = 0;

    console.log("🎯 Simulator started - sending WebSocket messages...");

    // Simulate connection opening
    setTimeout(() => {
      console.log("🔌 Simulating WebSocket connection open");
      if (this.wsClient.onOpen) {
        this.wsClient.onOpen();
      }
    }, 100);

    // Wait a bit to ensure display is ready before sending initial values
    setTimeout(() => {
      console.log("🕒 Waiting for display to be ready...");
      this.startSimulationLoop();
    }, 1000); // 1 second delay to ensure display is ready
  }

  public stop(): void {
    if (!this.isRunning) return;
    this.isRunning = false;

    // No longer using setInterval - cleanup handled by PixiJS ticker removal

    // Remove from PixiJS ticker
    Ticker.shared.remove(this.onTick);

    // Simulate connection closing
    if (this.wsClient.onClose) {
      this.wsClient.onClose(1000, "Simulation ended");
    }
  }

  /**
   * Set jackpot increase speed
   * @param speed Multiplier for increase speed (1.0 = normal, 0.5 = half speed, 2.0 = double speed)
   */
  public setIncreaseSpeed(speed: number): void {
    this.increaseSpeed = Math.max(0.1, Math.min(10.0, speed)); // Clamp between 0.1 and 10.0
    console.log(`⚡ Set jackpot increase speed to ${this.increaseSpeed}x`);
  }

  /**
   * Set jackpot increase range
   * @param min Minimum increase amount in dollars
   * @param max Maximum increase amount in dollars
   */
  public setIncreaseRange(min: number, max: number): void {
    this.minIncrease = Math.round(min * 100); // Convert dollars to cents
    this.maxIncrease = Math.round(max * 100); // Convert dollars to cents
    console.log(`💰 Set jackpot increase range: $${min} - $${max}`);
  }

  /**
   * Set update frequency
   * @param seconds Seconds between updates
   */
  public setUpdateFrequency(seconds: number): void {
    this.updateInterval = Math.max(500, Math.min(60000, seconds * 1000)); // Clamp between 0.5s and 60s
    console.log(`⏰ Set update frequency to ${seconds} seconds`);
  }

  /**
   * Set jackpot win interval for demo mode
   * @param seconds Seconds between automatic jackpot wins
   */
  public setWinInterval(seconds: number): void {
    this.winInterval = Math.max(30000, Math.min(600000, seconds * 1000)); // Clamp between 30s and 600s (10 minutes)
    console.log(`🎯 Set jackpot win interval to ${seconds} seconds`);
  }

  /**
   * Apply speed presets
   * @param preset "slow", "normal", "fast", or "turbo"
   */
  public applySpeedPreset(preset: "slow" | "normal" | "fast" | "turbo"): void {
    switch (preset) {
      case "slow":
        this.setIncreaseSpeed(0.5);
        this.setUpdateFrequency(3);
        this.setIncreaseRange(20, 200); // $20-$200 increases
        this.setWinInterval(300); // 5 minutes between wins
        break;
      case "normal":
        this.setIncreaseSpeed(1.0);
        this.setUpdateFrequency(2);
        this.setIncreaseRange(50, 1000); // $50-$1000 increases
        this.setWinInterval(180); // 3 minutes between wins
        break;
      case "fast":
        this.setIncreaseSpeed(2.0);
        this.setUpdateFrequency(1);
        this.setIncreaseRange(100, 2000); // $100-$2000 increases
        this.setWinInterval(120); // 2 minutes between wins
        break;
      case "turbo":
        this.setIncreaseSpeed(5.0);
        this.setUpdateFrequency(0.5);
        this.setIncreaseRange(500, 5000); // $500-$5000 increases
        this.setWinInterval(60); // 1 minute between wins
        break;
    }
    console.log(`🚀 Applied speed preset: ${preset}`);
  }

  /**
   * Get current jackpot speed statistics
   */
  public getSpeedStats(): {
    currentValue: number;
    lastIncrease: number;
    increasesPerMinute: number;
    speedMultiplier: number;
    updateFrequency: number;
    minIncrease: number;
    maxIncrease: number;
    winInterval: number;
  } {
    return {
      currentValue: this.currentJackpotValues[0]?.value / 100 || 0,
      lastIncrease: this.lastIncreaseAmount / 100,
      increasesPerMinute: this.increasesPerMinute,
      speedMultiplier: this.increaseSpeed,
      updateFrequency: this.updateInterval / 1000,
      minIncrease: this.minIncrease / 100,
      maxIncrease: this.maxIncrease / 100,
      winInterval: this.winInterval / 1000,
    };
  }

  private startSimulationLoop(): void {
    console.log("🎬 Starting simulation loop...");

    console.log("📤 Sending initial parameters message...");
    // Send initial parameters message
    this.sendParametersMessage();

    console.log("💰 Sending initial progressive values...");
    // Send initial progressive values
    this.sendProgressiveCurValuesMessage();

    // All timing is now handled by the PixiJS ticker in onTick method
    console.log(
      "⏰ All updates will be handled by PixiJS ticker (2 second intervals)",
    );

    // Send initial history after a brief delay to ensure display is ready (handled by ticker)
    console.log(
      "🕒 Initial history will be sent by ticker after 2 second delay",
    );
    this.historySent = false;
    this.historyDelayElapsed = 0;
  }

  private sendParametersMessage(): void {
    const message: WebSocketMessage = {
      msgType: MessageType.PARAMETERS,
      eventId: this.generateEventId(),
      mode: 1, // NO_CENTS
      values: this.simulationData?.jackpots || [
        { id: 1, jp: 10 },
        { id: 2, jp: 11 },
        { id: 3, jp: 12 },
      ],
    };

    this.sendMessage(message);
  }

  private sendProgressiveCurValuesMessage(): void {
    console.log("💰 Sending current jackpot value...");

    // Initialize with minimum value if this is the first time
    if (this.currentJackpotValues.length === 0) {
      this.currentJackpotValues = [
        {
          code: 10,
          value: this.jackpotMinValue, // Start at $15,000
        },
      ];
      console.log(
        "🆕 Initialized jackpot at minimum value: $",
        this.jackpotMinValue / 100,
      );
    }

    // Always send the current jackpot value
    const currentValue = this.currentJackpotValues[0].value;
    const message: WebSocketMessage = {
      msgType: MessageType.PROGRESSIVE_CUR_VALUES,
      values: [
        {
          code: 10,
          value: currentValue,
        },
      ],
    };

    console.log("📤 Sending PROGRESSIVE_CUR_VALUES:", currentValue / 100);
    this.sendMessage(message);
  }

  private checkForJackpotWin(): void {
    const currentTime = Date.now();
    const currentValue = this.currentJackpotValues[0].value;

    // Don't check for wins if cooldown is active
    if (this.lastHitTime > 0) {
      console.log("⏳ Win cooldown active - skipping win check");
      return;
    }

    // Time-based win triggering for demo purposes
    if (currentTime - this.lastWinTime >= this.winInterval) {
      console.log("🎉 TIME-BASED JACKPOT WIN! Value: $", currentValue / 100);

      // Send win message
      this.sendJackpotWinMessage(currentValue);

      // Reset jackpot to minimum value
      this.currentJackpotValues = [
        {
          code: 10,
          value: this.jackpotMinValue,
        },
      ];

      // Set hit cooldown
      this.lastHitTime = this.hitCooldown;

      // Update last win time
      this.lastWinTime = currentTime;

      // Record the win in history
      if (!this.simulationData.history) {
        this.simulationData.history = [];
      }
      this.simulationData.history.push({
        jp: 10,
        name: "MEGA JACKPOT",
        mach_id: Math.floor(Math.random() * 1000),
        timestamp: new Date().toISOString(),
        value: currentValue,
        location: `Ubicación ${String.fromCharCode(65 + Math.floor(Math.random() * 7))}`,
      });

      // Keep history at reasonable size
      if (this.simulationData.history.length > 10) {
        this.simulationData.history = this.simulationData.history.slice(-10);
      }
    } else {
      console.log("⏰ Waiting for next scheduled win...");
    }
  }

  private increaseJackpotValue(): void {
    const currentValue = this.currentJackpotValues[0].value;

    // Calculate increase amount with speed control
    const baseIncrease = Math.floor(
      Math.random() * (this.maxIncrease - this.minIncrease) + this.minIncrease,
    );
    const scaledIncrease = Math.round(baseIncrease * this.increaseSpeed);
    const newValue = Math.min(
      currentValue + scaledIncrease,
      this.jackpotMaxValue,
    );

    // Track performance metrics
    this.lastIncreaseAmount = scaledIncrease;

    // Calculate increases per minute (simple moving average)
    const now = Date.now();
    if (this.lastIncreaseTime > 0) {
      const timeSinceLast = now - this.lastIncreaseTime;
      const increasesPerMinute = 60000 / timeSinceLast;
      this.increasesPerMinute =
        this.increasesPerMinute * 0.9 + increasesPerMinute * 0.1; // Smooth average
    }
    this.lastIncreaseTime = now;

    console.log(
      `📈 Jackpot increase: $${currentValue / 100} → $${newValue / 100} (+$${scaledIncrease / 100})`,
    );
    console.log(
      `⚡ Speed: ${this.increaseSpeed}x, Rate: ${this.increasesPerMinute.toFixed(1)}/min`,
    );

    // Update the jackpot value
    this.currentJackpotValues = [
      {
        code: 10,
        value: newValue,
      },
    ];
  }

  private sendJackpotWinMessage(winAmount: number): void {
    console.log("🎁 Preparing jackpot win message for $", winAmount / 100);

    const jackpots = this.simulationData?.jackpots || [
      { id: 1, jp: 10, name: "MEGA JACKPOT" },
    ];

    if (jackpots.length === 0) return;

    const randomJackpot = jackpots[0]; // Use the first jackpot

    const message: WebSocketMessage = {
      msgType: MessageType.JACKPOT_HIT,
      eventId: this.generateEventId(),
      params: {
        jp: randomJackpot.jp,
        name: randomJackpot.name || "MEGA JACKPOT",
        mach_id: Math.floor(Math.random() * 1000),
        timestamp: new Date().toISOString(),
        value: winAmount,
        location: `Ubicación ${String.fromCharCode(65 + Math.floor(Math.random() * 7))}`,
      },
    };

    console.log("📤 Sending JACKPOT_HIT message:", message);
    this.sendMessage(message);
  }

  private sendJackpotHistoryMessage(): void {
    const historyItems = this.simulationData?.history || [
      {
        jp: 10,
        name: "MEGA JACKPOT",
        mach_id: 123,
        timestamp: new Date(Date.now() - 86400000).toISOString(), // Yesterday
        value: 5000000, // $50,000.00
        location: "Ubicación A",
      },
      {
        jp: 11,
        name: "MAJOR JACKPOT",
        mach_id: 456,
        timestamp: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
        value: 1000000, // $10,000.00
        location: "Ubicación B",
      },
    ];

    const message: WebSocketMessage = {
      msgType: MessageType.JACKPOT_HISTORY,
      eventId: this.generateEventId(),
      signId: GlobalStore.getSignId(),
      values: historyItems,
    };

    this.sendMessage(message);
  }

  private sendPeriodicUpdates(): void {
    console.log("🔄 Periodic update triggered by ticker");

    // First, check if we should trigger a jackpot win
    this.checkForJackpotWin();

    // Then, increase the jackpot value (unless we just had a win)
    if (this.lastHitTime === 0) {
      // Only increase if not in cooldown from a win
      this.increaseJackpotValue();
    }

    // Always send the current jackpot value
    this.sendProgressiveCurValuesMessage();
  }

  private sendRandomJackpotHit(): void {
    const jackpots = this.simulationData?.jackpots || [
      { id: 1, jp: 10, name: "MEGA JACKPOT" },
      { id: 2, jp: 11, name: "MAJOR JACKPOT" },
      { id: 3, jp: 12, name: "MINOR JACKPOT" },
    ];

    if (jackpots.length === 0) return;

    const randomJackpot = jackpots[Math.floor(Math.random() * jackpots.length)];
    const randomValue = Math.floor(Math.random() * 1000000) + 100000; // $1,000 to $10,000

    // Add this hit to the history for future updates
    const newHistoryItem = {
      jp: randomJackpot.jp,
      name: randomJackpot.name || "JACKPOT",
      mach_id: Math.floor(Math.random() * 1000),
      timestamp: new Date().toISOString(),
      value: randomValue,
      location: `Ubicación ${String.fromCharCode(65 + Math.floor(Math.random() * 7))}`,
    };

    // Update our local history to include this new hit
    if (!this.simulationData.history) {
      this.simulationData.history = [];
    }
    this.simulationData.history.push(newHistoryItem);
    // Keep history at a reasonable size (last 10 hits)
    if (this.simulationData.history.length > 10) {
      this.simulationData.history = this.simulationData.history.slice(-10);
    }

    const message: WebSocketMessage = {
      msgType: MessageType.JACKPOT_HIT,
      eventId: this.generateEventId(),
      params: newHistoryItem,
    };

    this.sendMessage(message);
  }

  private sendMessage(message: WebSocketMessage): void {
    console.log("📤 Sending simulated message:", message.msgType, message);
    if (this.wsClient.onMessage) {
      this.wsClient.onMessage(message);
    }
  }

  private generateEventId(): string {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  private getDefaultSimulationData(): any {
    return {
      updateInterval: 30000, // 30 seconds
      jackpots: [
        { id: 1, jp: 10, name: "MEGA JACKPOT" },
        { id: 2, jp: 11, name: "MAJOR JACKPOT" },
        { id: 3, jp: 12, name: "MINOR JACKPOT" },
      ],
      jackpotValues: [
        { code: 10, value: 5000000 }, // $50,000.00
        { code: 11, value: 2500000 }, // $25,000.00
        { code: 12, value: 1000000 }, // $10,000.00
      ],
      history: [
        {
          jp: 10,
          name: "MEGA JACKPOT",
          mach_id: 123,
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          value: 5000000,
          location: "Ubicación A",
        },
        {
          jp: 11,
          name: "MAJOR JACKPOT",
          mach_id: 456,
          timestamp: new Date(Date.now() - 172800000).toISOString(),
          value: 1000000,
          location: "Ubicación B",
        },
      ],
    };
  }

  private onTick = (ticker: Ticker): void => {
    this.updateElapsed += ticker.deltaMS;

    // Handle initial history delay (first 2 seconds)
    if (!this.historySent) {
      this.historyDelayElapsed += ticker.deltaMS;
      if (this.historyDelayElapsed >= this.historyDelay) {
        this.historySent = true;
        console.log(
          "📜 Sending initial jackpot history (after display init)...",
        );
        this.sendJackpotHistoryMessage();
      }
    }

    // Check if it's time for periodic updates (every 2 seconds)
    if (this.updateElapsed >= this.updateInterval) {
      this.updateElapsed -= this.updateInterval;
      this.sendPeriodicUpdates();
    }

    // Handle hit cooldown timing
    if (this.lastHitTime > 0) {
      this.lastHitTime -= ticker.deltaMS;
      if (this.lastHitTime <= 0) {
        this.lastHitTime = 0;
        console.log("⏳ Hit cooldown expired - ready for next jackpot win");
      }
    }
  };
}
